import React, { useState } from 'react';
import ToolLayout from '../components/ToolLayout';
// @ts-ignore
import ReactJson from 'react-json-view';

interface JwtPayload {
  header: any;
  payload: any;
  signature: string;
}

const JwtDecoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [decoded, setDecoded] = useState<JwtPayload | null>(null);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'interactive' | 'text'>('interactive');
  const [secretKey, setSecretKey] = useState('');
  const [algorithm, setAlgorithm] = useState('HS256');
  const [verificationResult, setVerificationResult] = useState<{
    isValid: boolean;
    message: string;
    error?: string;
  } | null>(null);

  const decodeJwt = () => {
    setError('');
    setDecoded(null);
    
    if (!input.trim()) {
      setError('Vui lòng nhập JWT token');
      return;
    }

    try {
      const parts = input.split('.');
      
      if (parts.length !== 3) {
        setError('JWT token không hợp lệ. Token phải có 3 phần được phân tách bởi dấu chấm.');
        return;
      }

      // Decode header
      const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
      
      // Decode payload  
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      
      // Signature (keep as is)
      const signature = parts[2];

      setDecoded({
        header,
        payload,
        signature
      });

    } catch (err) {
      setError('Không thể decode JWT token: ' + (err as Error).message);
    }
  };

  const clearAll = () => {
    setInput('');
    setDecoded(null);
    setError('');
    setSecretKey('');
    setVerificationResult(null);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Không thể copy vào clipboard');
    }
  };

  // Render JSON section with view mode toggle
  const renderJsonSection = (title: string, data: any, sectionClass: string) => {
    return (
      <div className="jwt-section">
        <div className="jwt-section-header">
          <h3 className="jwt-section-title">
            {title}:
          </h3>
          <div className="jwt-section-controls">
            {/* View Mode Toggle */}
            <div className="view-mode-toggle">
              <button
                className={`action-button ${viewMode === 'interactive' ? '' : 'secondary'}`}
                onClick={() => setViewMode('interactive')}
                style={{ fontSize: '0.7rem', padding: '0.4rem 0.6rem' }}
              >
                🎯
              </button>
              <button
                className={`action-button ${viewMode === 'text' ? '' : 'secondary'}`}
                onClick={() => setViewMode('text')}
                style={{ fontSize: '0.7rem', padding: '0.4rem 0.6rem' }}
              >
                📝
              </button>
            </div>
            <button
              className="action-button"
              style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
              onClick={() => copyToClipboard(JSON.stringify(data, null, 2))}
            >
              📋 Copy
            </button>
          </div>
        </div>

        {viewMode === 'interactive' ? (
          <div className={`jwt-json-container ${sectionClass}`}>
            <ReactJson
              src={data}
              theme="rjv-default"
              name={false}
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
              collapsed={1}
              indentWidth={2}
              iconStyle="triangle"
              style={{
                fontSize: '14px',
                fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                backgroundColor: 'transparent',
                lineHeight: '1.6',
                width: '100%'
              }}
            />
          </div>
        ) : (
          <div className={`jwt-json-text ${sectionClass}`}>
            <pre className="jwt-json-pre">
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('vi-VN');
  };

  // Available JWT algorithms
  const algorithms = [
    { value: 'HS256', label: 'HS256 (HMAC SHA-256)', type: 'symmetric' },
    { value: 'HS384', label: 'HS384 (HMAC SHA-384)', type: 'symmetric' },
    { value: 'HS512', label: 'HS512 (HMAC SHA-512)', type: 'symmetric' },
    { value: 'RS256', label: 'RS256 (RSA SHA-256)', type: 'asymmetric' },
    { value: 'RS384', label: 'RS384 (RSA SHA-384)', type: 'asymmetric' },
    { value: 'RS512', label: 'RS512 (RSA SHA-512)', type: 'asymmetric' },
    { value: 'ES256', label: 'ES256 (ECDSA SHA-256)', type: 'asymmetric' },
    { value: 'ES384', label: 'ES384 (ECDSA SHA-384)', type: 'asymmetric' },
    { value: 'ES512', label: 'ES512 (ECDSA SHA-512)', type: 'asymmetric' },
    { value: 'PS256', label: 'PS256 (RSA PSS SHA-256)', type: 'asymmetric' },
    { value: 'PS384', label: 'PS384 (RSA PSS SHA-384)', type: 'asymmetric' },
    { value: 'PS512', label: 'PS512 (RSA PSS SHA-512)', type: 'asymmetric' }
  ];

  // Helper function for base64url decode
  const base64UrlDecode = (str: string): ArrayBuffer => {
    // Add padding if needed
    const padded = str + '==='.slice((str.length + 3) % 4);
    // Convert base64url to base64
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
    // Decode base64 to ArrayBuffer
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Verify JWT signature using Web Crypto API
  const verifySignature = async () => {
    setVerificationResult(null);

    if (!input.trim()) {
      setVerificationResult({
        isValid: false,
        message: 'Vui lòng nhập JWT token',
        error: 'No token provided'
      });
      return;
    }

    if (!secretKey.trim()) {
      setVerificationResult({
        isValid: false,
        message: 'Vui lòng nhập secret key hoặc public key',
        error: 'No key provided'
      });
      return;
    }

    try {
      const parts = input.trim().split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      const [headerB64, payloadB64, signatureB64] = parts;
      const header = JSON.parse(atob(headerB64.replace(/-/g, '+').replace(/_/g, '/')));
      const selectedAlgorithm = header.alg || algorithm;

      // Create the data to verify (header.payload)
      const data = `${headerB64}.${payloadB64}`;
      const encoder = new TextEncoder();
      const dataUint8 = encoder.encode(data);
      const dataBuffer = dataUint8.buffer.slice(dataUint8.byteOffset, dataUint8.byteOffset + dataUint8.byteLength);

      // Decode the signature
      const signatureBuffer = base64UrlDecode(signatureB64);

      // Verify based on algorithm type
      if (selectedAlgorithm.startsWith('HS')) {
        // HMAC verification
        await verifyHMAC(dataBuffer, signatureBuffer, secretKey, selectedAlgorithm);
      } else if (selectedAlgorithm.startsWith('RS') || selectedAlgorithm.startsWith('PS') || selectedAlgorithm.startsWith('ES')) {
        // RSA/ECDSA verification
        await verifyAsymmetric(dataBuffer, signatureBuffer, secretKey, selectedAlgorithm);
      } else {
        throw new Error(`Unsupported algorithm: ${selectedAlgorithm}`);
      }

      setVerificationResult({
        isValid: true,
        message: `✅ JWT signature hợp lệ với algorithm ${selectedAlgorithm}!`
      });

    } catch (err) {
      const error = err as Error;
      let errorMessage = 'Signature không hợp lệ';
      
      if (error.message.includes('Invalid JWT format')) {
        errorMessage = 'Format JWT không hợp lệ';
      } else if (error.message.includes('Invalid key format')) {
        errorMessage = 'Format key không đúng - cần PEM format cho RSA/ECDSA';
      } else if (error.message.includes('Unsupported algorithm')) {
        errorMessage = 'Algorithm không được hỗ trợ';
      } else if (error.message.includes('verification failed')) {
        errorMessage = 'Signature không đúng - sai secret key hoặc algorithm';
      }

      setVerificationResult({
        isValid: false,
        message: `❌ ${errorMessage}`,
        error: error.message
      });
    }
  };

  // Verify HMAC signature
  const verifyHMAC = async (data: ArrayBuffer, signature: ArrayBuffer, secret: string, alg: string) => {
    const encoder = new TextEncoder();
    const secretBuffer = encoder.encode(secret);

    // Import secret key
    const key = await crypto.subtle.importKey(
      'raw',
      secretBuffer,
      { name: 'HMAC', hash: getHashAlgorithm(alg) },
      false,
      ['verify']
    );

    // Verify signature
    const isValid = await crypto.subtle.verify('HMAC', key, signature, data);
    if (!isValid) {
      throw new Error('HMAC verification failed');
    }
  };

  // Verify RSA/ECDSA signature  
  const verifyAsymmetric = async (data: ArrayBuffer, signature: ArrayBuffer, publicKeyPem: string, alg: string) => {
    try {
      // Parse PEM key
      const keyData = parsePEMKey(publicKeyPem);
      
      let algorithm: any;
      if (alg.startsWith('RS')) {
        algorithm = { name: 'RSASSA-PKCS1-v1_5', hash: getHashAlgorithm(alg) };
      } else if (alg.startsWith('PS')) {
        algorithm = { name: 'RSA-PSS', hash: getHashAlgorithm(alg), saltLength: getSaltLength(alg) };
      } else if (alg.startsWith('ES')) {
        algorithm = { name: 'ECDSA', hash: getHashAlgorithm(alg) };
      }

      // Import public key
      const key = await crypto.subtle.importKey(
        'spki',
        keyData,
        algorithm,
        false,
        ['verify']
      );

      // Verify signature
      const isValid = await crypto.subtle.verify(algorithm, key, signature, data);
      if (!isValid) {
        throw new Error('Asymmetric verification failed');
      }
    } catch (err) {
      throw new Error(`Invalid key format or verification failed: ${(err as Error).message}`);
    }
  };

  // Helper functions
  const getHashAlgorithm = (alg: string): string => {
    if (alg.includes('256')) {return 'SHA-256';}
    if (alg.includes('384')) {return 'SHA-384';}
    if (alg.includes('512')) {return 'SHA-512';}
    return 'SHA-256';
  };

  const getSaltLength = (alg: string): number => {
    if (alg.includes('256')) {return 32;}
    if (alg.includes('384')) {return 48;}
    if (alg.includes('512')) {return 64;}
    return 32;
  };

  const parsePEMKey = (pem: string): ArrayBuffer => {
    // Remove PEM headers and whitespace
    const b64 = pem
      .replace(/-----BEGIN [A-Z ]+-----/g, '')
      .replace(/-----END [A-Z ]+-----/g, '')
      .replace(/\s/g, '');
    
    // Decode base64
    const binaryString = atob(b64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  return (
    <ToolLayout 
      title="JWT Decoder" 
      description="Decode và phân tích JWT tokens để xem payload và header"
    >
      <div className="input-group">
        <label className="input-label">Nhập JWT Token:</label>
        <textarea
          className="input-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Nhập JWT token của bạn ở đây..."
          style={{ minHeight: '100px' }}
        />
      </div>

      <div className="button-group">
        <button className="action-button" onClick={decodeJwt}>
          Decode JWT
        </button>
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {decoded && (
        <div className="jwt-decoded-content">
          {/* Header */}
          {renderJsonSection('🔧 Header', decoded.header, 'jwt-header-section')}

          {/* Payload */}
          {renderJsonSection('📦 Payload', decoded.payload, 'jwt-payload-section')}

          {/* Show formatted timestamps if they exist */}
          {(decoded.payload.iat || decoded.payload.exp || decoded.payload.nbf) && (
            <div className="jwt-timestamp-section">
              <h4 className="jwt-timestamp-title">
                🕒 Thời gian (Formatted):
              </h4>
              <div className="jwt-timestamp-grid">
                {decoded.payload.iat && (
                  <div className="jwt-timestamp-item">
                    <span className="jwt-timestamp-label">Issued At (iat):</span>
                    <span className="jwt-timestamp-value">{formatTimestamp(decoded.payload.iat)}</span>
                  </div>
                )}
                {decoded.payload.exp && (
                  <div className="jwt-timestamp-item">
                    <span className="jwt-timestamp-label">Expires At (exp):</span>
                    <span className="jwt-timestamp-value">{formatTimestamp(decoded.payload.exp)}</span>
                  </div>
                )}
                {decoded.payload.nbf && (
                  <div className="jwt-timestamp-item">
                    <span className="jwt-timestamp-label">Not Before (nbf):</span>
                    <span className="jwt-timestamp-value">{formatTimestamp(decoded.payload.nbf)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Signature */}
          <div>
            <div className="jwt-section-header">
              <h3 className="jwt-section-title">
                🔒 Signature:
              </h3>
              <button
                className="action-button"
                style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
                onClick={() => copyToClipboard(decoded.signature)}
              >
                📋 Copy Signature
              </button>
            </div>
            <div className="jwt-signature-section">
              <code className="jwt-signature-code">
                {decoded.signature}
              </code>
              <div className="jwt-signature-warning">
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <span>Lưu ý: Signature được mã hóa và cần secret key để verify</span>
              </div>
            </div>
          </div>

          {/* JWT Verification Section */}
          <div className="jwt-verify-wrapper">
            <h3 className="jwt-section-title">
              🔐 Verify Signature:
            </h3>

            <div className="jwt-verify-section">
              {/* Algorithm Selection */}
              <div className="jwt-verify-field">
                <label className="jwt-verify-label">
                  🔧 Algorithm:
                </label>
                <select
                  value={algorithm}
                  onChange={(e) => setAlgorithm(e.target.value)}
                  className="jwt-verify-select"
                >
                  {algorithms.map((alg) => (
                    <option key={alg.value} value={alg.value}>
                      {alg.label}
                    </option>
                  ))}
                </select>
                {decoded?.header?.alg && (
                  <p className="jwt-verify-hint">
                    💡 Token header chỉ định algorithm: <strong>{decoded.header.alg}</strong>
                  </p>
                )}
              </div>

              {/* Secret Key Input */}
              <div className="jwt-verify-field">
                <label className="jwt-verify-label">
                  🔑 Secret Key {algorithms.find(a => a.value === algorithm)?.type === 'asymmetric' ? '(Public Key)' : '(Symmetric Key)'}:
                </label>
                <textarea
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={
                    algorithms.find(a => a.value === algorithm)?.type === 'asymmetric'
                      ? 'Nhập public key (PEM format cho RS*/ES*/PS*):\n-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----'
                      : 'Nhập secret key cho HMAC algorithms...'
                  }
                  className="jwt-verify-textarea"
                />
              </div>

              {/* Verify Button */}
              <button
                className="action-button"
                onClick={verifySignature}
                style={{ width: '100%', fontSize: '0.95rem' }}
              >
                🔍 Verify Signature
              </button>
            </div>

            {/* Verification Result */}
            {verificationResult && (
              <div className={`jwt-verify-result ${verificationResult.isValid ? 'success' : 'error'}`}>
                <div className="jwt-verify-result-message">
                  {verificationResult.message}
                </div>
                {verificationResult.error && (
                  <details className="jwt-verify-error-details">
                    <summary className="jwt-verify-error-summary">
                      🔍 Chi tiết lỗi
                    </summary>
                    <code className="jwt-verify-error-code">
                      {verificationResult.error}
                    </code>
                  </details>
                )}
              </div>
            )}

            {/* Help Information */}
            <div className="jwt-help-section">
              <h4 className="jwt-help-title">💡 Hướng dẫn:</h4>
              <ul className="jwt-help-list">
                <li><strong>HMAC (HS256/384/512):</strong> Dùng secret key đối xứng</li>
                <li><strong>RSA (RS256/384/512):</strong> Dùng public key để verify (private key để sign)</li>
                <li><strong>ECDSA (ES256/384/512):</strong> Dùng public key để verify</li>
                <li><strong>RSA-PSS (PS256/384/512):</strong> Dùng public key với PSS padding</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .jwt-decoded-content {
          margin-top: 2rem;
        }

        .jwt-section {
          margin-bottom: 2rem;
        }

        .jwt-section-header {
          display: flex;
          justify-content: space-between;
          alignitems: center;
          margin-bottom: 1rem;
        }

        .jwt-section-title {
          color: var(--text-primary);
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .jwt-section-controls {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .view-mode-toggle {
          display: flex;
          gap: 0.25rem;
          margin-right: 0.5rem;
        }

        .jwt-json-container {
          border: 2px solid var(--border-color);
          border-radius: 12px;
          padding: 0;
          background: var(--bg-secondary);
          overflow: hidden;
          text-align: left;
          width: 100%;
        }

        .jwt-json-container.jwt-header-section {
          border-color: var(--accent-primary);
        }

        .jwt-json-container.jwt-payload-section {
          border-color: #059669;
        }

        .react-json-view {
          background-color: var(--bg-secondary) !important;
          padding: 1.5rem !important;
          text-align: left !important;
        }

        .react-json-view .object-key-val {
          padding-left: 0 !important;
        }

        .react-json-view .object-content {
          margin-left: 0 !important;
          padding-left: 1rem !important;
        }

        .react-json-view .array-content {
          margin-left: 0 !important;
          padding-left: 1rem !important;
        }

        .jwt-json-text {
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
          padding: 1.5rem;
          text-align: left;
          width: 100%;
        }

        .jwt-json-text.jwt-header-section {
          border-color: var(--accent-primary);
        }

        .jwt-json-text.jwt-payload-section {
          border-color: #059669;
        }

        .jwt-json-pre {
          margin: 0;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 14px;
          white-space: pre-wrap;
          line-height: 1.6;
          color: var(--text-primary);
          text-align: left;
        }

        .jwt-timestamp-section {
          margin-top: -1rem;
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .jwt-timestamp-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .jwt-timestamp-grid {
          display: grid;
          gap: 0.75rem;
        }

        .jwt-timestamp-item {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem;
          background: var(--card-bg);
          border-radius: 8px;
          font-size: 0.95rem;
        }

        .jwt-timestamp-label {
          font-weight: 600;
          color: var(--text-primary);
        }

        .jwt-timestamp-value {
          color: var(--text-secondary);
        }

        .jwt-signature-section {
          background: var(--bg-secondary);
          border: 2px solid #f59e0b;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .jwt-signature-code {
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 14px;
          word-break: break-all;
          color: var(--text-primary);
          line-height: 1.6;
          display: block;
          padding: 1rem;
          background: var(--card-bg);
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .jwt-signature-warning {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: var(--text-secondary);
          font-weight: 500;
        }

        .jwt-verify-wrapper {
          margin-top: 2rem;
        }

        .jwt-verify-section {
          background: var(--bg-secondary);
          border: 2px solid var(--accent-primary);
          border-radius: 12px;
          padding: 1.5rem;
          margin: 1.5rem 0 1rem;
        }

        .jwt-verify-field {
          margin-bottom: 1rem;
        }

        .jwt-verify-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: var(--text-primary);
          font-size: 0.95rem;
        }

        .jwt-verify-select,
        .jwt-verify-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          font-size: 0.9rem;
          background: var(--bg-primary);
          color: var(--text-primary);
          transition: all 0.3s ease;
        }

        .jwt-verify-select {
          cursor: pointer;
        }

        .jwt-verify-select:focus,
        .jwt-verify-textarea:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .jwt-verify-textarea {
          min-height: 80px;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          resize: vertical;
        }

        .jwt-verify-textarea::placeholder {
          color: var(--text-tertiary);
        }

        .jwt-verify-hint {
          margin: 0.5rem 0 0 0;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .jwt-verify-result {
          padding: 1.5rem;
          border-radius: 12px;
          border: 2px solid;
          margin-bottom: 1rem;
        }

        .jwt-verify-result.success {
          border-color: #059669;
          background: var(--bg-secondary);
        }

        .jwt-verify-result.error {
          border-color: #dc2626;
          background: var(--bg-secondary);
        }

        .jwt-verify-result-message {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .jwt-verify-result.success .jwt-verify-result-message {
          color: #059669;
        }

        .jwt-verify-result.error .jwt-verify-result-message {
          color: #dc2626;
        }

        .jwt-verify-error-details {
          margin-top: 0.5rem;
        }

        .jwt-verify-error-summary {
          cursor: pointer;
          font-size: 0.9rem;
          color: var(--text-secondary);
          user-select: none;
        }

        .jwt-verify-error-code {
          display: block;
          margin-top: 0.5rem;
          padding: 0.75rem;
          background: var(--card-bg);
          border-radius: 6px;
          font-size: 0.85rem;
          color: #dc2626;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
        }

        .jwt-help-section {
          padding: 1rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 8px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .jwt-help-title {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
        }

        .jwt-help-list {
          margin: 0;
          padding-left: 1.2rem;
        }

        .jwt-help-list li {
          margin-bottom: 0.25rem;
        }

        @media (max-width: 768px) {
          .jwt-section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .jwt-section-controls {
            width: 100%;
            flex-direction: column;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default JwtDecoder;

